import React, {
  useCallback,
  useEffect,
  useRef,
  useContext,
  useState
} from "react";
import cn from "clsx";
import { isFunction } from "../../utils/helpers";
import useT from "../../hooks/useT";
import Button from "../Button";
import { CustomerPageContext } from "../../containers/CustomerPage";

type InternalRefObject =
  | React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>
  | React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;

export interface InputTextProps {
  /** Allows using `...inputProps` to pass HTMLInputElement/HTMLTextareaElement props to the input/text element itself */
  [propName: string]: any;
  id: string;
  name: string;
  type: string;
  value: string | null;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  placeholder?: string;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
  onChange?: React.ChangeEventHandler;
  onClear?: React.MouseEventHandler;
  onCustomError?: (error: string) => void;
  onClearCustomError?: () => void;
}

export const InputText = React.forwardRef(
  (
    {
      id,
      name,
      type,
      value,
      disabled,
      clearable,
      className,
      placeholder,
      onFocus,
      onBlur,
      onChange,
      onClear,
      onCustomError,
      onClearCustomError,
      ...inputProps
    }: InputTextProps,
    forwardedRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { t } = useT();
    const _value = value ? value : "";
    const internalRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
      null
    );
    const ref: InternalRefObject =
      !forwardedRef || isFunction(forwardedRef)
        ? internalRef
        : (forwardedRef as InternalRefObject);

    const [customerPage, _] = useContext(CustomerPageContext);

    const containsOneByteCharacter = (str: string) => {
      for (let i = 0; i < str.length; i++) {
        const char = str.charAt(i);
        const size = new Blob([char]).size;
        if (size === 1) {
          return true;
        }
      }
      return false;
    };

    const handleClickClear = useCallback(
      (event: React.MouseEvent) => {
        event.preventDefault();
        if (onClear) {
          onClear(event);
        }
      },
      [onClear, name]
    );

    useEffect(() => {
      // Special reset for date input type due to iOS not clearing
      // the value.
      if (type === "date" && ref && ref.current) {
        ref.current.defaultValue = "";
      }
    }, [type, _value]);

    const handleChange = useCallback(
      event => {
        event.preventDefault();
        // Allow only two bytes characters for katakana fields
        const katakanaFields = ["katakanaFirstname", "katakanaLastname"];
        if (katakanaFields.includes(id)) {
          const isContainsOneByte = containsOneByteCharacter(
            event.target.value
          );
          if (isContainsOneByte) {
            activateLables(
              onCustomError as Function,
              "customer.data.fields.katakana.name.errors.invalid"
            );
          } else {
            claerLables(onClearCustomError as Function);
          }
        }
        if (onChange) {
          onChange(event);
        }
      },
      [onChange, name]
    );

    function activateLables(functionToActivate: Function, translation: string) {
      if (functionToActivate) {
        functionToActivate(t(translation, { lng: customerPage.language }));
      }
    }

    function claerLables(functionToClear: Function) {
      if (functionToClear) {
        functionToClear();
      }
    }

    return (
      <div
        className={cn(
          "input input-text",
          className,
          !!value && "has-value",
          !!clearable && "is-clearable"
        )}
      >
        {type === "textarea" && (
          <textarea
            {...inputProps}
            id={id}
            name={name}
            value={_value}
            disabled={disabled}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={onChange}
            placeholder={placeholder}
            className="input-control input-text-control"
            ref={ref as React.RefObject<HTMLTextAreaElement>}
          />
        )}
        {type !== "textarea" && (
          <input
            {...inputProps}
            type={type}
            id={id}
            name={name}
            value={_value}
            autoComplete="off"
            disabled={disabled}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={handleChange}
            className="input-control input-text-control"
            placeholder={placeholder}
            ref={ref as React.RefObject<HTMLInputElement>}
          />
        )}
        {!!_value && clearable ? (
          <div className="input-actions input-text-actions">
            <Button
              icon="clear"
              ariaLabel={t("general.buttons.clear.label")}
              className="clear-input"
              onClick={handleClickClear}
            />
          </div>
        ) : null}
      </div>
    );
  }
);

InputText.displayName = "InputText";

export default InputText;
